import type { MarlinStoreDb } from "../infra/db";
import { notificationUtils } from "../logic/notificationRules";
import { getNotificationSettings } from "../settings/store";
import type {
  AddNotificationResult,
  Notification,
  NotificationFilter,
  NotificationRecord,
} from "../types";
import {
  assertValidNotification,
  notificationVocabularyIssue,
} from "./validate";

const matchesFilter = (
  notification: Notification,
  filter: NotificationFilter,
): boolean => {
  if (
    filter.sites?.length &&
    !filter.sites.includes(Number(notification.siteId))
  )
    return false;
  if (
    filter.cages?.length &&
    notification.cageId &&
    !filter.cages.some(
      (cage) =>
        cage.machineId === Number(notification.machineId) &&
        cage.cageId === Number(notification.cageId),
    )
  )
    return false;
  if (
    filter.cameras?.length &&
    notification.cameraId &&
    !filter.cameras.some(
      (camera) =>
        camera.machineId === Number(notification.machineId) &&
        camera.cameraId === Number(notification.cameraId),
    )
  )
    return false;
  if (filter.reasons?.length && !filter.reasons.includes(notification.subtype))
    return false;
  return true;
};

const isSimilar = (a: Notification, b: Notification): boolean =>
  a.type === b.type &&
  a.subtype === b.subtype &&
  a.machineId === b.machineId &&
  a.siteId === b.siteId &&
  a.cageId === b.cageId &&
  a.cameraId === b.cameraId &&
  a.feedline === b.feedline;

export const getNotifications = async (
  db: MarlinStoreDb,
  filter?: NotificationFilter,
): Promise<NotificationRecord[]> => {
  const all = await db.notifications.orderBy("rowId").reverse().toArray();
  return filter ? all.filter((n) => matchesFilter(n, filter)) : all;
};

export const addNotification = async (
  db: MarlinStoreDb,
  incoming: Notification,
): Promise<AddNotificationResult> => {
  assertValidNotification(incoming);
  const issue = notificationVocabularyIssue(incoming);
  if (issue) console.warn(`storing notification ${incoming.id}: ${issue}`);
  return db.transaction("rw", [db.notifications, db.settings], async () => {
    const settings = await getNotificationSettings(db);
    if (notificationUtils.isNotificationMuted(incoming, settings))
      return "suppressed";
    if (notificationUtils.shouldFilterByThreshold(incoming, settings))
      return "suppressed";

    const duplicate = await db.notifications
      .where("[machineId+id]")
      .equals([incoming.machineId, incoming.id])
      .first();
    if (duplicate) return "duplicate";

    const similar = (
      await db.notifications
        .where("[machineId+siteId+type+subtype]")
        .equals([
          incoming.machineId,
          incoming.siteId,
          incoming.type,
          incoming.subtype,
        ])
        .toArray()
    ).find((row) => isSimilar(row, incoming));
    if (similar?.rowId !== undefined)
      await db.notifications.delete(similar.rowId);

    const enriched: Notification = {
      ...incoming,
      alertType: notificationUtils.getAlertType(incoming, settings),
      critical: notificationUtils.getCritical(incoming, settings),
    };
    await db.notifications.add(enriched);
    return similar ? "merged" : "inserted";
  });
};

export const clearNotification = (
  db: MarlinStoreDb,
  id: number,
): Promise<void> =>
  db.notifications
    .filter((n) => n.id === id)
    .delete()
    .then(() => undefined);

export const clearNotificationsInBulk = (
  db: MarlinStoreDb,
  ids: number[],
): Promise<void> =>
  db.notifications
    .filter((n) => ids.includes(n.id))
    .delete()
    .then(() => undefined);

export const updateNotification = (
  db: MarlinStoreDb,
  machineId: number,
  id: number,
  updates: Partial<Notification>,
): Promise<void> =>
  db.transaction("rw", db.notifications, async () => {
    for (const row of await db.notifications
      .where("[machineId+id]")
      .equals([machineId, id])
      .toArray())
      await db.notifications.put({ ...row, ...updates } as NotificationRecord);
  });

export const soundFor = async (db: MarlinStoreDb, notification: Notification) =>
  notificationUtils.getNotificationSound(
    notification,
    await getNotificationSettings(db),
  );

export const getSoundNotifications = (
  db: MarlinStoreDb,
  afterRowId: number,
): Promise<NotificationRecord[]> =>
  db.notifications.where("rowId").above(afterRowId).toArray();
